import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { companyService, newsletterService, categoryService, type CompanyInfo } from '../services';
import type { Category } from '../types/Product';

const Footer: React.FC = () => {
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);

  // Load company info and categories on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load company info
        const companyResponse = await companyService.getCompanyInfo();
        if (companyResponse.success) {
          setCompanyInfo(companyResponse.data);
        }
        
        // Load categories
        const categoryResponse = await categoryService.getCategories();
        if (categoryResponse.success && categoryResponse.data) {
          // Get active categories, sort by displayOrder, and take top 5
          const activeCategories = categoryResponse.data
            .filter(cat => cat.isActive)
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .slice(0, 5);
          setCategories(activeCategories);
        }
      } catch (error) {
        console.error('Error loading footer data:', error);
      }
    };
    loadData();
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubscriptionError('Please enter a valid email address');
      return;
    }

    setIsSubscribing(true);
    setSubscriptionError('');

    try {
      const response = await newsletterService.subscribe({
        email
      });

      if (response.success) {
        setIsSubscribed(true);
        setEmail('');
        showToast('Successfully subscribed to newsletter!', 'success');
      } else {
        throw new Error(response.error || 'Failed to subscribe');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      setSubscriptionError(errorMessage);
      showToast('Failed to subscribe. Please try again.', 'error');
    } finally {
      setIsSubscribing(false);
    }
  };

  const InstagramIcon = () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );

  const FacebookIcon = () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );

  const TwitterIcon = () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
    </svg>
  );

  const YouTubeIcon = () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );

  // Unused component - commented out to fix build error
  // const SparkleIcon = () => (
  //   <svg className="w-6 h-6 text-accent-500" fill="currentColor" viewBox="0 0 24 24">
  //     <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
  //   </svg>
  // );

  return (
    <footer className="bg-neutral-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-6">
              
              <h3 className="text-2xl font-bold text-white">Demo Crackers</h3>
            </div>
            
            <p className="text-neutral-300 leading-relaxed mb-8 max-w-sm">
              Your trusted partner for premium quality firecrackers and fireworks. 
              Safe, certified, and spectacular celebrations await.
            </p>
            <div className="flex gap-4">
              {companyInfo?.socialMedia.map((social) => {
                const renderIcon = () => {
                  switch (social.platform.toLowerCase()) {
                    case 'facebook':
                      return <FacebookIcon />;
                    case 'instagram':
                      return <InstagramIcon />;
                    case 'x':
                    case 'twitter':
                      return <TwitterIcon />;
                    case 'youtube':
                      return <YouTubeIcon />;
                    case 'whatsapp':
                      return (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                      );
                    default:
                      return <FacebookIcon />;
                  }
                };

                return (
                  <a 
                    key={social.platform}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label={social.platform} 
                    className="bg-neutral-800 hover:bg-accent-600 text-neutral-300 hover:text-white p-3 rounded-full transition-all duration-300 transform hover:scale-110"
                  >
                    {renderIcon()}
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">Home</Link></li>
              <li><Link to="/about" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">About Us</Link></li>
              <li><Link to="/contact" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">Contact</Link></li>
              <li><Link to="/shipping" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">Shipping Info</Link></li>
              {/* <li><Link to="/returns" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">Returns</Link></li> */}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Categories</h4>
            <ul className="space-y-3">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <li key={category.id}>
                    <Link 
                      to={`/category/${category.slug}`} 
                      className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {category.name}
                    </Link>
                  </li>
                ))
              ) : (
                <>
                  <li><Link to="/products" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">All Products</Link></li>
                  <li><Link to="/products?tag=new" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">New Arrivals</Link></li>
                  <li><Link to="/products?tag=featured" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">Featured</Link></li>
                  <li><Link to="/products?tag=bestseller" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">Best Sellers</Link></li>
                  <li><Link to="/products?tag=sale" className="text-neutral-300 hover:text-accent-500 transition-all duration-300 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">On Sale</Link></li>
                </>
              )}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Contact Info</h4>
            <div className="space-y-4 text-neutral-300">
              {companyInfo && (
                <>
                  {/* Head Office Address */}
                  {companyInfo.addresses.find(addr => addr.type === 'registered') && (
                    <div className="flex items-start gap-3">
                      <div className="bg-neutral-800 p-2 rounded-lg">
                        <svg className="w-5 h-5 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span className="text-sm leading-relaxed">
                        {companyInfo.addresses.find(addr => addr.type === 'registered')?.addressLine1}<br />
                        {companyInfo.addresses.find(addr => addr.type === 'registered')?.city}, {companyInfo.addresses.find(addr => addr.type === 'registered')?.pincode}
                      </span>
                    </div>
                  )}
                  
                  {/* Sales Phone */}
                  {companyInfo.contacts.find(c => c.type === 'sales') && (
                    <div className="flex items-center gap-3">
                      <div className="bg-neutral-800 p-2 rounded-lg">
                        <svg className="w-5 h-5 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <span className="text-sm">{companyInfo.contacts.find(c => c.type === 'sales')?.phone}</span>
                    </div>
                  )}
                  
                  {/* General Email */}
                  {companyInfo.contacts.find(c => c.type === 'general') && (
                    <div className="flex items-center gap-3">
                      <div className="bg-neutral-800 p-2 rounded-lg">
                        <svg className="w-5 h-5 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm">{companyInfo.contacts.find(c => c.type === 'general')?.email}</span>
                    </div>
                  )}
                  
                  {/* Business Hours */}
                  <div className="flex items-center gap-3">
                    <div className="bg-neutral-800 p-2 rounded-lg">
                      <svg className="w-5 h-5 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-sm">
                      {companyInfo.businessHours.filter(h => !h.isClosed).map(h => h.day.slice(0, 3)).join('-')}: 
                      {' '}{companyInfo.businessHours.find(h => !h.isClosed)?.open} - {companyInfo.businessHours.find(h => !h.isClosed)?.close}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="bg-gradient-to-r from-accent-600 to-accent-500 rounded-2xl p-6 sm:p-8 mb-12">
          <div className="max-w-md mx-auto text-center">
            {!isSubscribed ? (
              <>
                <h4 className="text-xl font-bold text-white mb-2">Stay Updated</h4>
                <p className="text-accent-100 mb-6 text-sm">Get the latest deals and safety tips delivered to your inbox</p>
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 sm:gap-2">
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email" 
                    className="flex-1 px-4 py-3 rounded-xl border-0 bg-white/20 backdrop-blur-sm text-white placeholder-accent-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                    disabled={isSubscribing}
                    required
                  />
                  <button 
                    type="submit"
                    disabled={isSubscribing}
                    className="bg-white text-accent-600 px-6 py-3 rounded-xl font-semibold hover:bg-accent-50 transition-colors duration-300 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubscribing ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Subscribing...
                      </div>
                    ) : (
                      'Subscribe'
                    )}
                  </button>
                </form>
                {subscriptionError && (
                  <p className="text-red-200 text-sm mt-3 bg-red-500/20 rounded-lg px-3 py-2">
                    {subscriptionError}
                  </p>
                )}
              </>
            ) : (
              <div className="animate-fade-in-up">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h4 className="text-xl font-bold text-white mb-2">You're All Set! 🎆</h4>
                <p className="text-accent-100 mb-4 text-sm">
                  Thank you for subscribing! You'll receive our latest deals and safety tips in your inbox.
                </p>
                <button 
                  onClick={() => setIsSubscribed(false)}
                  className="text-accent-100 hover:text-white text-sm underline transition-colors duration-300"
                >
                  Subscribe another email
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-neutral-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-neutral-400 text-sm">&copy; 2024 Demo Crackers. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-8">
              <Link to="/privacy" className="text-neutral-400 hover:text-accent-500 transition-colors text-sm relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-neutral-400 hover:text-accent-500 transition-colors text-sm relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">
                Terms of Service
              </Link>
              <Link to="/safety" className="text-neutral-400 hover:text-accent-500 transition-colors text-sm relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-accent-500 after:transition-all after:duration-300 hover:after:w-full">
                Safety Guidelines
              </Link>
            </div>
          </div>
          
          {/* Developer Credits */}
          <div className="mt-6 pt-6 border-t border-neutral-800">
            <p className="text-center text-neutral-500 text-sm">
              Developed and maintained by{' '}
              <a 
                href="https://unitednexa.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent-500 hover:text-accent-400 transition-colors duration-300 font-medium"
              >
                United Nexa Tech
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;